import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useOrg } from '../../context/OrgContext';
import { getAllSeasonTimes, type SeasonTime } from '../../services/seasonTime.service';
import { getAllCropTypes, type CropType } from '../../services/cropType.service';
import { getAllRegions, type Region } from '../../services/region.service';
import { getCustomerCropsForStatus } from '../../services/cropStatus.service';
import { getCustomerById, type Customer } from '../../services/customer.service';
import { getSeasonStatus } from '../../utils/seasonStatus';
import { Navbar } from '../../components/Navbar';
import { BackButton, EmptyState, SkeletonCard } from '../../components/core';
import { FilterBar } from './FilterBar';
import { ResultsTable } from './ResultsTable';
import { ResultCards } from './ResultCards';
import { TimelineView } from './TimelineView';
import {
  type CropStatusFilters,
  type ResultRow,
  type EmptyReason,
  STORAGE_KEY,
  DEFAULT_FILTERS,
  UPCOMING_THRESHOLD_OPTIONS,
} from './types';
import { filterAndDeduplicateCrops } from './cropStatusUtils';

// ─── localStorage helpers ─────────────────────────────────────────────────────

function loadFilters(): CropStatusFilters {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const p = JSON.parse(raw) as Record<string, unknown>;
    if (
      typeof p === 'object' &&
      p !== null &&
      typeof p.status === 'string' &&
      typeof p.cropTypeId === 'string' &&
      typeof p.regionId === 'string'
    ) {
      const savedThreshold = typeof p.upcomingThresholdDays === 'number' && UPCOMING_THRESHOLD_OPTIONS.includes(p.upcomingThresholdDays as typeof UPCOMING_THRESHOLD_OPTIONS[number])
        ? p.upcomingThresholdDays as number
        : DEFAULT_FILTERS.upcomingThresholdDays;
      return {
        status: p.status as CropStatusFilters['status'],
        cropTypeId: p.cropTypeId as string,
        regionId: p.regionId as string,
        groupByCropType: typeof p.groupByCropType === 'boolean' ? p.groupByCropType : true,
        upcomingThresholdDays: savedThreshold,
        viewMode: p.viewMode === 'timeline' ? 'timeline' : 'table',
      };
    }
    return DEFAULT_FILTERS;
  } catch {
    return DEFAULT_FILTERS;
  }
}

function saveFilters(f: CropStatusFilters) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(f));
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function CropStatusPage() {
  const { user, signOut } = useAuth();
  const { org, orgId, orgSlug } = useOrg();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Reference data (loaded once on mount)
  const [cropTypes, setCropTypes] = useState<CropType[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [seasonTimes, setSeasonTimes] = useState<SeasonTime[]>([]);
  const [refLoading, setRefLoading] = useState(true);

  // Filters
  const [filters, setFilters] = useState<CropStatusFilters>(() => loadFilters());

  // Results
  const [results, setResults] = useState<ResultRow[]>([]);
  const [queryLoading, setQueryLoading] = useState(false);
  const [emptyReason, setEmptyReason] = useState<EmptyReason>('no-status');
  const [loadError, setLoadError] = useState(false);

  // Grouping UI state
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // ─── Load reference data on mount ────────────────────────────────────────
  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const [cts, regs, sts] = await Promise.all([
          getAllCropTypes(orgId),
          getAllRegions(orgId),
          getAllSeasonTimes(orgId),
        ]);
        setCropTypes(cts);
        setRegions(regs);
        setSeasonTimes(sts);
      } catch (err) {
        console.error('[CropStatusPage] load reference data error:', err);
      } finally {
        setRefLoading(false);
      }
    })();
  }, [orgId]);

  // ─── Run query whenever filters or reference data change ─────────────────
  const runQuery = useCallback(async (f: CropStatusFilters, sts: SeasonTime[]) => {
    // In timeline mode, allow empty status (show all). In table mode, require status.
    if (!orgId || (!f.status && f.viewMode !== 'timeline')) {
      setResults([]);
      setEmptyReason('no-status');
      return;
    }

    // Filter season times by optional crop type / region selectors
    let matchingSeasons = sts;
    if (f.cropTypeId) matchingSeasons = matchingSeasons.filter(s => s.cropTypeId === f.cropTypeId);
    if (f.regionId)   matchingSeasons = matchingSeasons.filter(s => s.regionId   === f.regionId);

    // Keep only seasons whose current status matches the selected status filter
    // In timeline mode with empty status, include all statuses
    const today = new Date();
    if (f.status) {
      const thresholdDays = f.status === 'upcoming' ? f.upcomingThresholdDays : undefined;
      matchingSeasons = matchingSeasons.filter(s => getSeasonStatus(s, today, thresholdDays) === f.status);
    }

    if (matchingSeasons.length === 0) {
      setResults([]);
      setEmptyReason('no-seasons');
      return;
    }

    setQueryLoading(true);
    setLoadError(false);
    try {
      // Fast-path: no optional filters → single unfiltered query covers everything
      // Otherwise fire one query per matching (cropTypeId, regionId) pair in parallel
      const allCropResults: Awaited<ReturnType<typeof getCustomerCropsForStatus>>[] =
        !f.cropTypeId && !f.regionId
          ? [await getCustomerCropsForStatus(orgId, {})]
          : await Promise.all(
              matchingSeasons.map(s =>
                getCustomerCropsForStatus(orgId, { cropTypeId: s.cropTypeId, regionId: s.regionId }),
              ),
            );

      // Flatten, filter to valid (cropTypeId, regionId) pairs, and deduplicate.
      // The fast-path returns all crops unfiltered, so this guard is critical.
      const unique = filterAndDeduplicateCrops(allCropResults, matchingSeasons);

      if (unique.length === 0) {
        setResults([]);
        setEmptyReason('no-customers');
        return;
      }

      // Batch-fetch unique customer IDs
      const uniqueCustomerIds = [...new Set(unique.map(u => u.customerId))];
      const customerResults = await Promise.all(uniqueCustomerIds.map(id => getCustomerById(orgId, id)));

      const customerMap = new Map<string, Customer>();
      for (const c of customerResults) {
        if (c && !c.isDeleted) customerMap.set(c.id, c);
      }

      // Build result rows (one per matching crop assignment), sorted by lastName
      const rows: ResultRow[] = unique
        .filter(u => customerMap.has(u.customerId))
        .map(u => ({
          customer: customerMap.get(u.customerId)!,
          cropTypeId: u.cropTypeId,
          regionId: u.regionId,
        }))
        .sort((a, b) => a.customer.lastName.localeCompare(b.customer.lastName));

      if (rows.length === 0) {
        setResults([]);
        setEmptyReason('no-customers');
      } else {
        setResults(rows);
      }
    } catch (err) {
      console.error('[CropStatusPage] query error:', err);
      setLoadError(true);
      setResults([]);
    } finally {
      setQueryLoading(false);
    }
  }, [orgId]);

  // Re-run whenever filters or season times change (season times are stable after mount)
  useEffect(() => {
    if (refLoading) return;
    runQuery(filters, seasonTimes);
  }, [filters, seasonTimes, refLoading, runQuery]);

  // ─── Filter handlers ──────────────────────────────────────────────────────
  function handleFilterChange(patch: Partial<CropStatusFilters>) {
    setFilters(prev => {
      const next = { ...prev, ...(patch ?? {}) };
      saveFilters(next);
      return next;
    });
  }

  // Switch to table view with pre-applied filters (called from timeline tooltip)
  function handleViewCustomers(cropTypeId: string, regionId: string, status: string) {
    handleFilterChange({ viewMode: 'table', cropTypeId, regionId, status: status as CropStatusFilters['status'] });
  }

  function handleClearFilters() {
    saveFilters(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
    setResults([]);
    setEmptyReason('no-status');
  }

  // Reset collapsed groups whenever results change (new query)
  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [results]);

  function handleToggleGroup(cropTypeId: string) {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(cropTypeId)) next.delete(cropTypeId);
      else next.add(cropTypeId);
      return next;
    });
  }

  // ─── Lookup helpers ───────────────────────────────────────────────────────
  function cropTypeName(id: string) {
    return cropTypes.find(ct => ct.id === id)?.name ?? id;
  }

  function regionName(id: string) {
    return regions.find(r => r.id === id)?.name ?? id;
  }

  const statusLabel =
    filters.status === 'upcoming'  ? t('cropStatus.statusUpcoming') :
    filters.status === 'in-season' ? t('cropStatus.statusInSeason') :
    '';

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-green-50">
      <Navbar displayName={user?.displayName ?? null} onSignOut={signOut} onLogoClick={() => navigate('/')} onOrgNameClick={() => navigate(`/org/${orgSlug}`)} onAvatarClick={() => navigate('/profile')} orgName={org.name} />

      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
        <BackButton />

        {/* Heading */}
        <div className="mb-5">
          <h1 className="font-display text-xl font-semibold text-gray-900">{t('cropStatus.title')}</h1>
          <p className="mt-1 font-body text-sm text-gray-500">{t('cropStatus.subtitle')}</p>
        </div>

        <FilterBar
          filters={filters}
          cropTypes={cropTypes}
          regions={regions}
          disabled={refLoading}
          onChange={handleFilterChange}
          onClear={handleClearFilters}
        />

        {/* Body */}
        {filters.viewMode === 'timeline' ? (
          refLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : (
            <TimelineView
              seasonTimes={seasonTimes}
              cropTypes={cropTypes}
              regions={regions}
              results={results}
              filters={filters}
              onViewCustomers={handleViewCustomers}
            />
          )
        ) : loadError ? (
          <div className="rounded-lg bg-red-50 px-4 py-3 font-body text-sm text-red-600">
            {t('cropStatus.loadError')}
          </div>
        ) : queryLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : results.length > 0 ? (
          <>
            {/* Results meta: count + Group by Crop Type toggle */}
            <div className="mb-3 flex items-center justify-between">
              <p className="font-body text-sm text-gray-500">
                {t('cropStatus.resultsCount', { count: results.length })}
              </p>
              <button
                type="button"
                role="switch"
                aria-checked={filters.groupByCropType}
                onClick={() => handleFilterChange({ groupByCropType: !filters.groupByCropType })}
                className="flex items-center gap-2 font-body text-sm text-gray-600 hover:text-gray-800"
              >
                <span>{t('cropStatus.groupByCropType')}</span>
                <div className={`relative h-5 w-9 rounded-full transition-colors duration-200 ${filters.groupByCropType ? 'bg-green-600' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-[inset-inline-start] duration-200 ${filters.groupByCropType ? 'start-[18px]' : 'start-0.5'}`} />
                </div>
              </button>
            </div>
            <ResultsTable
              results={results}
              cropTypeName={cropTypeName}
              regionName={regionName}
              groupByCropType={filters.groupByCropType}
              collapsedGroups={collapsedGroups}
              onToggleGroup={handleToggleGroup}
              onViewCustomer={id => navigate(`/org/${orgSlug}/customers/${id}`, { state: { from: `/org/${orgSlug}/crop-status` } })}
              isUpcoming={filters.status === 'upcoming'}
            />
            <ResultCards
              results={results}
              cropTypeName={cropTypeName}
              regionName={regionName}
              groupByCropType={filters.groupByCropType}
              collapsedGroups={collapsedGroups}
              onToggleGroup={handleToggleGroup}
              onViewCustomer={id => navigate(`/org/${orgSlug}/customers/${id}`, { state: { from: `/org/${orgSlug}/crop-status` } })}
              isUpcoming={filters.status === 'upcoming'}
            />
          </>
        ) : emptyReason === 'no-status' ? (
          <EmptyState
            emoji="🌾"
            title={t('cropStatus.emptyNoStatus')}
            description={t('cropStatus.emptyNoStatusHelp')}
          />
        ) : emptyReason === 'no-seasons' ? (
          <EmptyState
            emoji="📅"
            title={t('cropStatus.emptyNoSeasons', { status: statusLabel })}
            description={t('cropStatus.emptyNoSeasonsHelp')}
          />
        ) : (
          <EmptyState
            emoji="👥"
            title={t('cropStatus.emptyNoCustomers')}
            description={t('cropStatus.emptyNoCustomersHelp')}
          />
        )}
      </main>
    </div>
  );
}
